import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, UserCheck, FileText, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={true} />
      
      {/* Hero Section */}
      <section className="relative bg-[#0d6571] pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:text-white/80 mb-8 -ml-4"
          >
            <ArrowLeft className="mr-2" />
            Back to Home
          </Button>
          
          <div className="flex items-center gap-4 mb-6">
            <Shield className="w-16 h-16 text-[#d97841]" />
            <h1 className="text-5xl md:text-6xl font-bold text-white">
              Privacy Policy
            </h1>
          </div>
          
          <p className="text-xl text-white/90 max-w-4xl leading-relaxed">
            How we collect, use, and protect your personal information
          </p>
          <p className="text-sm text-white/70 mt-4">
            Last Updated: November 2024
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Introduction */}
          <div className="mb-16">
            <div className="bg-gradient-to-br from-[#0d6571]/5 to-[#0d6571]/10 border-l-4 border-[#0d6571] p-8 rounded-r-lg">
              <p className="text-lg text-gray-700 leading-relaxed">
                At FXC Management Services B.V., we are committed to protecting your privacy and ensuring 
                the security of your personal information. This Privacy Policy explains how we collect, 
                use, disclose, and safeguard your information when you visit our website or use our services.
              </p>
            </div>
          </div>

          {/* Information We Collect */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-8 h-8 text-[#d97841]" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Information We Collect
              </h2>
            </div>
            
            <div className="space-y-6">
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Personal Information</h3>
                <p className="text-gray-600 leading-relaxed mb-3">
                  We may collect personal information that you voluntarily provide to us when you:
                </p>
                <ul className="space-y-2 text-gray-600 ml-6">
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">•</span>
                    <span>Register for an account or subscribe to our services</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">•</span>
                    <span>Contact us through forms or email</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">•</span>
                    <span>Request information about our services</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">•</span>
                    <span>Participate in surveys or promotions</span>
                  </li>
                </ul>
                <p className="text-gray-600 mt-3">
                  This information may include: name, email address, phone number, company name, 
                  job title, and any other information you choose to provide.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Automatically Collected Information</h3>
                <p className="text-gray-600 leading-relaxed mb-3">
                  When you visit our website, we automatically collect certain information about your device:
                </p>
                <ul className="space-y-2 text-gray-600 ml-6">
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">•</span>
                    <span>IP address and browser type</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">•</span>
                    <span>Device information and operating system</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">•</span>
                    <span>Pages visited and time spent on pages</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">•</span>
                    <span>Referring website and navigation patterns</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* How We Use Your Information */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <UserCheck className="w-8 h-8 text-[#d97841]" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                How We Use Your Information
              </h2>
            </div>
            
            <div className="bg-gradient-to-r from-white to-[#0d6571]/5 border border-gray-200 rounded-xl p-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                We use the information we collect for various purposes, including:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-start">
                    <span className="text-[#d97841] mr-2 text-xl">✓</span>
                    <span className="text-gray-700">Providing and maintaining our services</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-[#d97841] mr-2 text-xl">✓</span>
                    <span className="text-gray-700">Responding to your inquiries and requests</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-[#d97841] mr-2 text-xl">✓</span>
                    <span className="text-gray-700">Sending newsletters and marketing communications</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-[#d97841] mr-2 text-xl">✓</span>
                    <span className="text-gray-700">Improving our website and services</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <span className="text-[#d97841] mr-2 text-xl">✓</span>
                    <span className="text-gray-700">Analyzing usage patterns and trends</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-[#d97841] mr-2 text-xl">✓</span>
                    <span className="text-gray-700">Protecting against fraudulent activities</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-[#d97841] mr-2 text-xl">✓</span>
                    <span className="text-gray-700">Complying with legal obligations</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-[#d97841] mr-2 text-xl">✓</span>
                    <span className="text-gray-700">Personalizing your experience</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Data Security */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <Lock className="w-8 h-8 text-[#d97841]" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Data Security
              </h2>
            </div>
            
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                We implement appropriate technical and organizational security measures to protect your 
                personal information against unauthorized access, alteration, disclosure, or destruction.
              </p>
              <p className="text-gray-600 leading-relaxed">
                These measures include encryption, secure servers, access controls, and regular security 
                assessments. However, no method of transmission over the internet or electronic storage 
                is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>
          </div>

          {/* Your Rights */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <Eye className="w-8 h-8 text-[#d97841]" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Your Privacy Rights
              </h2>
            </div>
            
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-white to-[#0d6571]/5 border-l-4 border-[#0d6571] p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Access and Correction</h3>
                <p className="text-gray-600">You have the right to access and update your personal information.</p>
              </div>
              
              <div className="bg-gradient-to-r from-white to-[#0d6571]/5 border-l-4 border-[#0d6571] p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Data Portability</h3>
                <p className="text-gray-600">You can request a copy of your personal data in a structured format.</p>
              </div>
              
              <div className="bg-gradient-to-r from-white to-[#0d6571]/5 border-l-4 border-[#0d6571] p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Right to Erasure</h3>
                <p className="text-gray-600">You can request deletion of your personal information, subject to legal requirements.</p>
              </div>
              
              <div className="bg-gradient-to-r from-white to-[#0d6571]/5 border-l-4 border-[#0d6571] p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Opt-Out</h3>
                <p className="text-gray-600">You can unsubscribe from marketing communications at any time.</p>
              </div>
            </div>
          </div>

          {/* Cookies */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Cookies and Tracking Technologies
            </h2>
            
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to track activity on our website and 
                store certain information. Cookies are small data files stored on your device.
              </p>
              <p className="text-gray-700 leading-relaxed">
                You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. 
                However, if you do not accept cookies, you may not be able to use some portions of our website.
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-[#0d6571] rounded-2xl p-12 text-white">
            <div className="flex items-center gap-3 mb-6">
              <AlertCircle className="w-10 h-10 text-[#d97841]" />
              <h2 className="text-3xl md:text-4xl font-bold">
                Questions About Privacy?
              </h2>
            </div>
            <p className="text-lg text-white/90 mb-6">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-white/10 rounded-xl p-6">
              <p className="mb-2"><strong>FXC Management Services B.V.</strong></p>
              <p className="mb-2">Industrieweg 9, 2254AE Voorschoten, The Netherlands</p>
              <p className="mb-2">Email: <a href="mailto:info@fxcvantage.com" className="text-[#d97841] hover:underline">info@fxcvantage.com</a></p>
              <p>Chamber of Commerce: 78204992</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;